import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, DollarSign, Save, LogOut } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import VedLogo from '../../components/VedLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPricing = () => {
  const navigate = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [customPrice, setCustomPrice] = useState('');
  const [available, setAvailable] = useState(true);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('pricing'); // 'pricing' or 'availability'
  const username = localStorage.getItem('admin_username');

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchRooms();
  }, []);

  const fetchRooms = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/rooms`);
      setRooms(response.data);
      if (response.data.length > 0) {
        setSelectedRoom(response.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching rooms:', error);
      toast.error('Failed to load rooms');
    }
  };

  const handleSavePricing = async () => {
    if (!selectedRoom || !startDate) {
      toast.error('Please select a room and start date');
      return;
    }

    if (mode === 'pricing' && !customPrice) {
      toast.error('Please enter a custom price');
      return;
    }

    const finalEndDate = endDate || startDate;
    setLoading(true);

    try {
      const token = localStorage.getItem('admin_token');
      const start = new Date(startDate);
      const end = new Date(finalEndDate);
      const dates = [];

      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        dates.push(d.toISOString().split('T')[0]);
      }

      const selectedRoomData = rooms.find(r => r.id === selectedRoom);
      const priceToSave = mode === 'pricing' 
        ? parseFloat(customPrice) 
        : selectedRoomData?.price || 0;

      const promises = dates.map(date =>
        axios.post(
          `${BACKEND_URL}/api/admin/pricing`,
          {
            room_id: selectedRoom,
            date: date,
            price: priceToSave,
            available: mode === 'pricing' ? true : available
          },
          { headers: { Authorization: `Bearer ${token}` } }
        )
      );

      await Promise.all(promises);

      // Also update room's available field directly if blocking/unblocking
      if (mode === 'availability') {
        await axios.patch(
          `${BACKEND_URL}/api/admin/rooms/${selectedRoom}`,
          { available: available },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      const message = dates.length === 1
        ? `Updated successfully for 1 date!`
        : `Updated successfully for ${dates.length} dates!`;

      toast.success(message);
      setStartDate('');
      setEndDate('');
      setCustomPrice('');
      setAvailable(true);
    } catch (error) {
      console.error('Error saving:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        handleLogout();
      } else {
        toast.error('Failed to save changes');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_username');
    navigate('/admin/login');
  };

  const selectedRoomData = rooms.find(r => r.id === selectedRoom);
  const minDate = new Date().toISOString().split('T')[0];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Link
              to="/admin/dashboard"
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft size={24} className="text-gray-700" />
            </Link>
            <VedLogo size="sm" showText={false} />
            <div>
              <h1 className="text-xl font-bold text-gray-800">Room Management</h1>
              <p className="text-sm text-gray-600">Manage pricing and availability</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center space-x-2 px-4 py-2 text-gray-700 hover:text-red-600 transition-colors"
          >
            <LogOut size={20} />
            <span>Logout</span>
          </button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8">

        {/* Mode Toggle */}
        <div className="flex gap-3 mb-6">
          <button
            onClick={() => setMode('pricing')}
            className={`flex-1 py-3 rounded-lg font-semibold transition-colors ${
              mode === 'pricing'
                ? 'bg-orange-600 text-white'
                : 'bg-white text-gray-600 border hover:border-orange-400'
            }`}
          >
            💰 Set Custom Pricing
          </button>
          <button
            onClick={() => setMode('availability')}
            className={`flex-1 py-3 rounded-lg font-semibold transition-colors ${
              mode === 'availability'
                ? 'bg-orange-600 text-white'
                : 'bg-white text-gray-600 border hover:border-orange-400'
            }`}
          >
            🚫 Manage Availability
          </button>
        </div>

        {/* Room Status Overview */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Current Room Status</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {rooms.map(room => (
              <div key={room.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="font-medium text-gray-800 text-sm">{room.name}</span>
                <div className="flex items-center gap-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    room.available 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {room.available ? 'Available' : 'Unavailable'}
                  </span>
                  <span className="text-sm text-gray-600">₹{room.price}/night</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Form */}
        <div className="bg-white rounded-xl shadow-sm p-8">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <DollarSign className="text-orange-600" size={24} />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                {mode === 'pricing' ? 'Set Custom Pricing' : 'Manage Availability'}
              </h2>
              <p className="text-sm text-gray-600">
                {mode === 'pricing' 
                  ? 'Override default room prices for specific dates'
                  : 'Mark rooms as available or unavailable'
                }
              </p>
            </div>
          </div>

          <div className="space-y-6">
            {/* Room Selection */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Select Room
              </label>
              <select
                value={selectedRoom}
                onChange={(e) => setSelectedRoom(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                {rooms.map((room) => (
                  <option key={room.id} value={room.id}>
                    {room.name} - Default: ₹{room.price}/night
                  </option>
                ))}
              </select>
            </div>

            {/* Date Range */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Calendar size={16} className="inline mr-1" />
                  Start Date *
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  min={minDate}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <Calendar size={16} className="inline mr-1" />
                  End Date (Optional)
                </label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  min={startDate || minDate}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>
            </div>

            {/* Pricing Mode Fields */}
            {mode === 'pricing' && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Custom Price (₹) *
                </label>
                <input
                  type="number"
                  value={customPrice}
                  onChange={(e) => setCustomPrice(e.target.value)}
                  placeholder={selectedRoomData ? `Default: ₹${selectedRoomData.price}` : 'Enter price'}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  min="0"
                  step="100"
                />
              </div>
            )}

            {/* Availability Mode Fields */}
            {mode === 'availability' && (
              <div className="space-y-3">
                <label className="block text-sm font-semibold text-gray-700">
                  Availability Status
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setAvailable(true)}
                    className={`py-3 rounded-lg font-medium border-2 transition-colors ${
                      available
                        ? 'bg-green-50 border-green-500 text-green-700'
                        : 'bg-white border-gray-300 text-gray-600'
                    }`}
                  >
                    ✓ Available
                  </button>
                  <button
                    onClick={() => setAvailable(false)}
                    className={`py-3 rounded-lg font-medium border-2 transition-colors ${
                      !available
                        ? 'bg-red-50 border-red-500 text-red-700'
                        : 'bg-white border-gray-300 text-gray-600'
                    }`}
                  >
                    ✗ Unavailable
                  </button>
                </div>
              </div>
            )}

            {/* Save Button */}
            <button
              onClick={handleSavePricing}
              disabled={loading}
              className="w-full px-6 py-4 bg-orange-600 text-white rounded-lg font-semibold hover:bg-orange-700 transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
            >
              <Save size={20} />
              <span>{loading ? 'Saving...' : 'Save Changes'}</span>
            </button>
          </div>
        </div>

        {/* Info Box */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-3">How it works:</h3>
          <ul className="space-y-2 text-sm text-gray-700">
            <li>✓ Use <strong>Set Custom Pricing</strong> to change room prices for specific dates</li>
            <li>✓ Use <strong>Manage Availability</strong> to mark rooms as available or unavailable</li>
            <li>✓ Set a date range to update multiple dates at once</li>
            <li>✓ Custom prices override the default room price for guests searching those dates</li>
            <li>✓ Unavailable rooms will not show up in search results</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AdminPricing;
